import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  FiAlertTriangle,
  FiBox,
  FiRefreshCcw,
  FiRepeat,
  FiTrendingDown,
  FiTrendingUp,
} from "react-icons/fi";

import API from "../../../../../../utils/apiCalling";
import { config } from "../../../../../../utils/apiUrl";
import useSwal from "../../../../../../hooks/useSwal";

import StockMovementTable from "./StockMovementTable";
import StockAdjustmentDialog from "./StockAdjustmentDialog";
import StockReconcileDialog from "./StockReconcileDialog";
import StockHistoryDrawer from "./StockHistoryDrawer";

import "./StockManagement.css";

const EMPTY_FILTERS = {
  search: "",
  productId: "",
  movementType: "",
  referenceType: "",
  dateFrom: "",
  dateTo: "",
  sortOrder: "desc",
};

const EMPTY_PAGINATION = {
  total: 0,
  page: 1,
  limit: 10,
  totalPages: 1,
};

const EMPTY_SUMMARY = {
  totalMovements: 0,
  totalStockIn: 0,
  totalStockOut: 0,
  movementTypes: {},
};

const MOVEMENT_TYPES = [
  {
    value: "",
    label: "All Movements",
  },
  {
    value: "opening_stock",
    label: "Opening Stock",
  },
  {
    value: "stock_in",
    label: "Stock In",
  },
  {
    value: "stock_out",
    label: "Stock Out",
  },
  {
    value: "sale",
    label: "Sale",
  },
  {
    value: "sale_cancelled",
    label: "Sale Cancelled",
  },
  {
    value: "customer_return",
    label: "Customer Return",
  },
  {
    value: "supplier_return",
    label: "Supplier Return",
  },
  {
    value: "damaged",
    label: "Damaged",
  },
  {
    value: "lost",
    label: "Lost",
  },
  {
    value: "adjustment_in",
    label: "Adjustment In",
  },
  {
    value: "adjustment_out",
    label: "Adjustment Out",
  },
];

const REFERENCE_TYPES = [
  {
    value: "",
    label: "All References",
  },
  {
    value: "product",
    label: "Product",
  },
  {
    value: "purchase",
    label: "Purchase",
  },
  {
    value: "order",
    label: "Order",
  },
  {
    value: "return",
    label: "Return",
  },
  {
    value: "manual",
    label: "Manual",
  },
  {
    value: "other",
    label: "Other",
  },
];

const formatNumber = (value) => {
  return new Intl.NumberFormat("en-IN").format(
    Number(value || 0)
  );
};

const StockManagement = () => {
  const api = useMemo(() => new API(), []);
  const swal = useSwal();

  const [movements, setMovements] =
    useState([]);

  const [products, setProducts] =
    useState([]);

  const [filters, setFilters] = useState({
    ...EMPTY_FILTERS,
  });

  const [
    appliedFilters,
    setAppliedFilters,
  ] = useState({
    ...EMPTY_FILTERS,
  });

  const [pagination, setPagination] =
    useState({
      ...EMPTY_PAGINATION,
    });

  const [summary, setSummary] = useState({
    ...EMPTY_SUMMARY,
  });

  const [loading, setLoading] =
    useState(false);

  const [
    productLoading,
    setProductLoading,
  ] = useState(false);

  const [error, setError] =
    useState("");

  const [
    adjustmentOpen,
    setAdjustmentOpen,
  ] = useState(false);

  const [
    reconcileOpen,
    setReconcileOpen,
  ] = useState(false);

  const [
    selectedProduct,
    setSelectedProduct,
  ] = useState(null);

  const [
    historyProduct,
    setHistoryProduct,
  ] = useState(null);

  const fetchProducts = useCallback(
    async () => {
      try {
        setProductLoading(true);

        const response = await api.post(
          config.getInventoryProducts,
          {
            page: 1,
            limit: 100,
            status: "",
            search: "",
            sortBy: "name",
            sortOrder: "asc",
          }
        );

        if (
          response?.status === "success"
        ) {
          setProducts(
            Array.isArray(response?.data)
              ? response.data
              : []
          );
        } else {
          setProducts([]);
        }
      } catch (requestError) {
        console.error(
          "Get stock product dropdown error:",
          requestError
        );

        setProducts([]);
      } finally {
        setProductLoading(false);
      }
    },
    [api]
  );

  const fetchMovements = useCallback(
    async () => {
      try {
        setLoading(true);
        setError("");

        const payload = {
          page: pagination.page,
          limit: pagination.limit,

          search:
            appliedFilters.search?.trim() ||
            "",

          productId:
            appliedFilters.productId || "",

          movementType:
            appliedFilters.movementType ||
            "",

          referenceType:
            appliedFilters.referenceType ||
            "",

          dateFrom:
            appliedFilters.dateFrom || "",

          dateTo:
            appliedFilters.dateTo || "",

          sortOrder:
            appliedFilters.sortOrder ||
            "desc",
        };

        const response = await api.post(
          config.getInventoryStockMovements,
          payload
        );

        if (
          response?.status === "success"
        ) {
          setMovements(
            Array.isArray(response?.data)
              ? response.data
              : []
          );

          setPagination((previous) => ({
            ...previous,

            total: Number(
              response?.pagination?.total ||
                0
            ),

            page: Number(
              response?.pagination?.page ||
                previous.page
            ),

            limit: Number(
              response?.pagination?.limit ||
                previous.limit
            ),

            totalPages: Math.max(
              Number(
                response?.pagination
                  ?.totalPages || 1
              ),
              1
            ),
          }));

          setSummary({
            ...EMPTY_SUMMARY,
            ...(response?.summary || {}),
          });
        } else {
          setMovements([]);

          setPagination((previous) => ({
            ...previous,
            total: 0,
            totalPages: 1,
          }));

          setSummary({
            ...EMPTY_SUMMARY,
          });

          setError(
            response?.message ||
              "Unable to fetch stock movements."
          );
        }
      } catch (requestError) {
        console.error(
          "Get stock movements error:",
          requestError
        );

        setMovements([]);

        setPagination((previous) => ({
          ...previous,
          total: 0,
          totalPages: 1,
        }));

        setSummary({
          ...EMPTY_SUMMARY,
        });

        setError(
          requestError?.response?.data
            ?.message ||
            requestError?.message ||
            "Unable to fetch stock movements."
        );
      } finally {
        setLoading(false);
      }
    },
    [
      api,
      appliedFilters,
      pagination.page,
      pagination.limit,
    ]
  );

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  useEffect(() => {
    fetchMovements();
  }, [fetchMovements]);

  const handleFilterChange = (
    event
  ) => {
    const { name, value } =
      event.target;

    setFilters((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const handleSearch = () => {
    if (
      filters.dateFrom &&
      filters.dateTo &&
      new Date(filters.dateFrom) >
        new Date(filters.dateTo)
    ) {
      swal.warning({
        icon: "warning",
        title: "Invalid Date Range",
        text:
          "From date cannot be greater than to date.",
      });

      return;
    }

    setAppliedFilters({
      ...filters,
    });

    setPagination((previous) => ({
      ...previous,
      page: 1,
    }));
  };

  const handleReset = () => {
    setFilters({
      ...EMPTY_FILTERS,
    });

    setAppliedFilters({
      ...EMPTY_FILTERS,
    });

    setPagination((previous) => ({
      ...previous,
      page: 1,
    }));
  };

  const handleOpenAdjustment = (
    product = null
  ) => {
    setSelectedProduct(product);
    setAdjustmentOpen(true);
  };

  const handleOpenReconcile = (
    product = null
  ) => {
    setSelectedProduct(product);
    setReconcileOpen(true);
  };

  const handleOpenHistory = (
    product
  ) => {
    setHistoryProduct(product);
  };

  const handleActionSuccess = () => {
    setAdjustmentOpen(false);
    setReconcileOpen(false);
    setSelectedProduct(null);

    fetchProducts();
    fetchMovements();
  };

  const handlePageChange = (
    page
  ) => {
    if (
      page < 1 ||
      page > pagination.totalPages
    ) {
      return;
    }

    setPagination((previous) => ({
      ...previous,
      page,
    }));
  };

  const handleLimitChange = (
    event
  ) => {
    setPagination((previous) => ({
      ...previous,
      page: 1,
      limit: Number(
        event.target.value
      ),
    }));
  };

  const summaryCards = [
    {
      key: "movements",
      title: "Total Movements",
      value: formatNumber(
        summary.totalMovements
      ),
      subtitle:
        "Recorded stock transactions",
      icon: FiRepeat,
      className:
        "inventory-stock-summary-blue",
    },
    {
      key: "stock-in",
      title: "Total Stock In",
      value: formatNumber(
        summary.totalStockIn
      ),
      subtitle:
        "Incoming inventory units",
      icon: FiTrendingUp,
      className:
        "inventory-stock-summary-green",
    },
    {
      key: "stock-out",
      title: "Total Stock Out",
      value: formatNumber(
        summary.totalStockOut
      ),
      subtitle:
        "Outgoing inventory units",
      icon: FiTrendingDown,
      className:
        "inventory-stock-summary-red",
    },
    {
      key: "products",
      title: "Inventory Products",
      value: formatNumber(
        products.length
      ),
      subtitle:
        "Available for stock management",
      icon: FiBox,
      className:
        "inventory-stock-summary-purple",
    },
  ];

  return (
    <div className="inventory-stock-page">
      <div className="inventory-stock-header">
        <div>
          <span>
            STOCK MANAGEMENT
          </span>

          <h3>
            Stock Movements
          </h3>

          <p>
            Add stock, reduce stock,
            reconcile physical inventory and
            review stock history.
          </p>
        </div>

        <div className="inventory-stock-header-actions">
          <button
            type="button"
            className="inventory-stock-refresh"
            onClick={() => {
              fetchProducts();
              fetchMovements();
            }}
            disabled={
              loading ||
              productLoading
            }
            title="Refresh stock data"
          >
            <FiRefreshCcw
              className={
                loading ||
                productLoading
                  ? "spinning"
                  : ""
              }
            />
          </button>

          <button
            type="button"
            className="inventory-stock-reconcile-button"
            onClick={() =>
              handleOpenReconcile()
            }
          >
            <FiRepeat />
            Reconcile Stock
          </button>

          <button
            type="button"
            className="inventory-stock-adjust-button"
            onClick={() =>
              handleOpenAdjustment()
            }
          >
            <FiBox />
            Adjust Stock
          </button>
        </div>
      </div>

      <div className="inventory-stock-summary-grid">
        {summaryCards.map((card) => {
          const Icon = card.icon;

          return (
            <div
              key={card.key}
              className={`inventory-stock-summary-card ${card.className}`}
            >
              <div className="inventory-stock-summary-icon">
                <Icon />
              </div>

              <div className="inventory-stock-summary-content">
                <span>
                  {card.title}
                </span>

                <strong>
                  {loading
                    ? "..."
                    : card.value}
                </strong>

                <small>
                  {card.subtitle}
                </small>
              </div>
            </div>
          );
        })}
      </div>

      <div className="inventory-stock-filter-card">
        <div className="inventory-stock-filter-grid">
          <div className="inventory-stock-filter-field inventory-stock-search-field">
            <label>
              Search
            </label>

            <input
              type="text"
              name="search"
              value={filters.search}
              onChange={
                handleFilterChange
              }
              onKeyDown={(event) => {
                if (
                  event.key === "Enter"
                ) {
                  handleSearch();
                }
              }}
              placeholder="Search product, SKU, reference or notes"
              disabled={loading}
            />
          </div>

          <div className="inventory-stock-filter-field">
            <label>
              Product
            </label>

            <select
              name="productId"
              value={filters.productId}
              onChange={
                handleFilterChange
              }
              disabled={
                loading ||
                productLoading
              }
            >
              <option value="">
                All Products
              </option>

              {products.map(
                (product) => (
                  <option
                    key={product._id}
                    value={product._id}
                  >
                    {product.name}
                    {product.sku
                      ? ` (${product.sku})`
                      : ""}
                  </option>
                )
              )}
            </select>
          </div>

          <div className="inventory-stock-filter-field">
            <label>
              Movement Type
            </label>

            <select
              name="movementType"
              value={
                filters.movementType
              }
              onChange={
                handleFilterChange
              }
              disabled={loading}
            >
              {MOVEMENT_TYPES.map(
                (movementType) => (
                  <option
                    key={
                      movementType.value
                    }
                    value={
                      movementType.value
                    }
                  >
                    {movementType.label}
                  </option>
                )
              )}
            </select>
          </div>

          <div className="inventory-stock-filter-field">
            <label>
              Reference Type
            </label>

            <select
              name="referenceType"
              value={
                filters.referenceType
              }
              onChange={
                handleFilterChange
              }
              disabled={loading}
            >
              {REFERENCE_TYPES.map(
                (referenceType) => (
                  <option
                    key={
                      referenceType.value
                    }
                    value={
                      referenceType.value
                    }
                  >
                    {referenceType.label}
                  </option>
                )
              )}
            </select>
          </div>

          <div className="inventory-stock-filter-field">
            <label>
              From Date
            </label>

            <input
              type="date"
              name="dateFrom"
              value={filters.dateFrom}
              onChange={
                handleFilterChange
              }
              disabled={loading}
            />
          </div>

          <div className="inventory-stock-filter-field">
            <label>
              To Date
            </label>

            <input
              type="date"
              name="dateTo"
              value={filters.dateTo}
              onChange={
                handleFilterChange
              }
              disabled={loading}
            />
          </div>

          <div className="inventory-stock-filter-field">
            <label>
              Sort Order
            </label>

            <select
              name="sortOrder"
              value={
                filters.sortOrder
              }
              onChange={
                handleFilterChange
              }
              disabled={loading}
            >
              <option value="desc">
                Newest First
              </option>

              <option value="asc">
                Oldest First
              </option>
            </select>
          </div>

          <div className="inventory-stock-filter-actions">
            <button
              type="button"
              className="inventory-stock-search-button"
              onClick={handleSearch}
              disabled={loading}
            >
              Search
            </button>

            <button
              type="button"
              className="inventory-stock-reset-button"
              onClick={handleReset}
              disabled={loading}
            >
              Reset
            </button>
          </div>
        </div>
      </div>

      {error ? (
        <div className="inventory-stock-error">
          <FiAlertTriangle />
          {error}
        </div>
      ) : null}

      <StockMovementTable
        movements={movements}
        loading={loading}
        pagination={pagination}
        onPageChange={
          handlePageChange
        }
        onLimitChange={
          handleLimitChange
        }
        onOpenHistory={
          handleOpenHistory
        }
        onAdjustStock={
          handleOpenAdjustment
        }
        onReconcileStock={
          handleOpenReconcile
        }
      />

      <StockAdjustmentDialog
        open={adjustmentOpen}
        product={selectedProduct}
        products={products}
        onClose={() => {
          setAdjustmentOpen(false);
          setSelectedProduct(null);
        }}
        onSuccess={
          handleActionSuccess
        }
      />

      <StockReconcileDialog
        open={reconcileOpen}
        product={selectedProduct}
        products={products}
        onClose={() => {
          setReconcileOpen(false);
          setSelectedProduct(null);
        }}
        onSuccess={
          handleActionSuccess
        }
      />

      <StockHistoryDrawer
        open={Boolean(
          historyProduct
        )}
        product={historyProduct}
        onClose={() =>
          setHistoryProduct(null)
        }
        onAdjustStock={(
          product
        ) => {
          setHistoryProduct(null);
          handleOpenAdjustment(
            product
          );
        }}
        onReconcileStock={(
          product
        ) => {
          setHistoryProduct(null);
          handleOpenReconcile(
            product
          );
        }}
      />
    </div>
  );
};

export default StockManagement;